/**
 * Groups API Client
 *
 * API client for managing groups (top-level grouping of sources).
 */

import { httpClient } from "./client";
import {
  assertObject,
  assertEntityId,
  assertString,
  assertNumber,
  assertOptionalString,
  assertArrayOf,
} from "../types/validation";

// ============================================================================
// Types
// ============================================================================

export interface IGroup {
  readonly id: string;
  readonly projectId: string;
  readonly name: string;
  readonly description?: string;
  readonly sourceCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ICreateGroupRequest {
  readonly name: string;
  readonly description?: string;
}

export interface IUpdateGroupRequest {
  readonly name: string;
  readonly description?: string;
}

// ============================================================================
// Validation
// ============================================================================

export function validateGroup(data: unknown, path: string): IGroup {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    projectId: assertEntityId(obj['projectId'], `${path}.projectId`),
    name: assertString(obj['name'], `${path}.name`),
    description: assertOptionalString(obj['description'], `${path}.description`),
    sourceCount: assertNumber(obj['sourceCount'], `${path}.sourceCount`),
    createdAt: assertString(obj['createdAt'], `${path}.createdAt`),
    updatedAt: assertString(obj['updatedAt'], `${path}.updatedAt`),
  };
}

// ============================================================================
// API Client Interface
// ============================================================================

export interface IGroupsApiClient {
  listGroups(projectId: string): Promise<IGroup[]>;
  getGroup(groupId: string): Promise<IGroup>;
  createGroup(projectId: string, request: ICreateGroupRequest): Promise<IGroup>;
  updateGroup(groupId: string, request: IUpdateGroupRequest): Promise<IGroup>;
  deleteGroup(groupId: string): Promise<void>;
}

// ============================================================================
// Error class
// ============================================================================

export class GroupApiError extends Error {
  readonly code: string;
  readonly params?: Record<string, unknown>;

  constructor(code: string, message: string, params?: Record<string, unknown>) {
    super(message);
    this.name = 'GroupApiError';
    this.code = code;
    this.params = params;
  }
}

// ============================================================================
// Implementation
// ============================================================================

export class GroupsApiClient implements IGroupsApiClient {
  async listGroups(projectId: string): Promise<IGroup[]> {
    const response = await httpClient.get(`/projects/${projectId}/groups`);
    return assertArrayOf(response.data, (item, path) => validateGroup(item, path), 'groups');
  }

  async getGroup(groupId: string): Promise<IGroup> {
    const response = await httpClient.get(`/groups/${groupId}`);
    return validateGroup(response.data, 'group');
  }

  async createGroup(projectId: string, request: ICreateGroupRequest): Promise<IGroup> {
    const response = await httpClient.post(`/projects/${projectId}/groups`, request);
    return validateGroup(response.data, 'group');
  }

  async updateGroup(groupId: string, request: IUpdateGroupRequest): Promise<IGroup> {
    const response = await httpClient.put(`/groups/${groupId}`, request);
    return validateGroup(response.data, 'group');
  }

  async deleteGroup(groupId: string): Promise<void> {
    await httpClient.delete(`/groups/${groupId}`);
  }

}
